import { useState, useEffect } from 'react';
import { CompanyDetailedDto } from 'models/dtoTypes';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { GetCurrentCompany } from 'services/user';
import EsgScoreUpdateButton from 'components/EsgScoreUpdateButton';
// import Typography from '@mui/material/Typography';
import { getSustainabilityScoreColor } from 'kosmosConfig';
import { getSustainabilityScore } from 'utils/score';
import downloadKosmosReport from 'utils/downloadKosmosReport';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SmeEsgRatings';

const classes = {
  root: `${PREFIX}-root`,
  score: `${PREFIX}-score`,
  buttonContainer: `${PREFIX}-buttonContainer`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
    flexGrow: 1,
    height: 'fit-content',
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(6),
  },

  [`& .${classes.buttonContainer}`]: {
    textAlign: 'center',
    width: 150,
    marginRight: 15,
  },

  [`& .${classes.score}`]: {
    cursor: 'pointer',
    backgroundColor: '#24c8a8',
    color: 'white',
    fontWeight: 'bold',
    width: 150,
    height: 80,
    border: '2px solid #24c8a8',
    padding: 0,
  },
}));

function SmeEsgRatings(): JSX.Element {
  const [company, setCompany] = useState<CompanyDetailedDto>();
  const [statusInfo, setStatusInfo] = useState<string>(company?.statusInfo || '');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { t } = useTranslation('default');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await GetCurrentCompany();
        console.log(res);
        setCompany(res);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    // company.scoringState:  Pending(not started), InPorgress(started), Error, Active
    // state(statusInfo):     Request info        , Pending,             Error, Activ3
    // NOTE: click trigger button => setStatusInfo('Requesting')
    // Note: get first reply from api(start loading) => setStatusInfo('Pending')
    if (scoringState === 'Error') {
      setStatusInfo('Error');
    } else if (statusInfo === 'Pending') {
      setStatusInfo('Request info');
    } else if (statusInfo === 'Active') {
      setStatusInfo('Active');
    }
  }, [company]);

  const { id, scoringState = 'NotStarted' } = company || {};
  const isDissolved = statusInfo === 'Dissolved';
  const score = getSustainabilityScore(company);

  const generateScoreWidgetStyle = () => {
    if (isDissolved) {
      return {
        backgroundColor: '#707071',
        border: '2px solid #707071',
      };
    } else if (statusInfo === 'Request info') {
      return {
        backgroundColor: 'white',
        border: '2px solid #24c8a8',
        color: '#24c8a8',
      };
    } else if (scoringState === 'Completed') {
      const color = getSustainabilityScoreColor(score || 0);
      return {
        border: `2px solid ${color || 'black'}`,
        backgroundColor: color || 'black',
      };
    } else {
      return {};
    }
  };

  const generateScoringState = () => {
    if (loading || statusInfo === 'Requesting') {
      return 'InProgress';
    }
    return isDissolved ? '' : scoringState;
  };

  const handleComplete = (company: CompanyDetailedDto) => {
    setCompany(company);
  };

  const handleSelectCompany = async (id: string) => {
    if (scoringState !== 'Completed' || isDissolved) {
      return;
    }
    if (loading) {
      return;
    }

    // await savePdf(elementRef.current, filename);
    setDownloading(true);
    try {
      const name = company?.name || 'name unidentified';
      const filename = `${name} - Coriolis Sustainability Report -${id}.pdf`;
      await downloadKosmosReport({
        companyId: id,
        defaultFilename: filename,
        pdfStyle: 'CoriolisEsg',
        reportType: 'Summary',
      });
    } catch (e) {
      console.log(e);
    }
    setDownloading(false);
  };

  const renderHelperText = () => {
    if (scoringState !== 'Completed') {
      return (
        <div>
          <div>Please wait whilst your ESG Ratings are calculated. </div>
          <div>
            Calculations can sometimes take a while, you can leave this page and check back
            later.{' '}
          </div>
        </div>
      );
    } else {
      return <div>Click the score to download the report.</div>;
    }
  };

  if (loading) {
    return (
      <Root className={classes.root}>
        <CircularProgress />
      </Root>
    );
  }
  if (company) {
    return (
      <Root className={classes.root}>
        <div className={classes.buttonContainer}>
          <div style={{ marginBottom: 10 }}>
            <b>ESG score</b>
          </div>
          <div
            className={classes.score}
            style={generateScoreWidgetStyle()}
            onClick={() => handleSelectCompany(id || '')}
          >
            <EsgScoreUpdateButton
              initialScore={score}
              initialState={generateScoringState()} // treat dissolved company scoringState to ''
              company={company}
              disabled={isDissolved}
              onComplete={handleComplete}
              onStarted={() => setStatusInfo('Requesting')}
            />
          </div>
          <div>
            <b>{t('status')}:</b> {statusInfo || ''}
          </div>
        </div>

        <div>{downloading ? <div>Downloading the report PDF...</div> : renderHelperText()}</div>
      </Root>
    );
  } else {
    return <div>Cannot find your linked company</div>;
  }
}

export default SmeEsgRatings;
